import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import Iconify from '../iconify';
import * as shareService from '../../services/shareService';
import { errorNotification, infoNotification } from '../../services/notificationService';

export default function ShareDialog({ open, onClose, dataCaptureId, ownerId, userOrganizationId }) {
  const [shares, setShares] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedGrantee, setSelectedGrantee] = useState(null);
  const [userPermission, setUserPermission] = useState('viewer');
  const [orgPermission, setOrgPermission] = useState('viewer');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const hasOrg = Boolean(userOrganizationId);
  const orgAlreadyShared = shares.some(
    (s) => s.granteeType === 'organization' && s.granteeId === userOrganizationId
  );

  const loadShares = useCallback(async () => {
    if (!dataCaptureId) return;
    try {
      setLoading(true);
      const data = await shareService.getSharesForRecording(dataCaptureId);
      setShares(data);
    } catch (error) {
      errorNotification(error.response?.data?.message || 'Failed to load shares');
    } finally {
      setLoading(false);
    }
  }, [dataCaptureId]);

  useEffect(() => {
    if (open) {
      loadShares();
      setSelectedGrantee(null);
      setSearchQuery('');
      setSearchResults([]);
      setUserPermission('viewer');
      setOrgPermission('viewer');
    }
  }, [open, loadShares]);

  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      setSearchLoading(true);
      const results = await shareService.searchUsers(query);
      setSearchResults(results);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => handleSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const handleShareWithUser = async () => {
    if (!selectedGrantee) return;
    try {
      await shareService.createShare(dataCaptureId, selectedGrantee.id, 'user', userPermission);
      infoNotification('Shared successfully');
      setSelectedGrantee(null);
      setSearchQuery('');
      setSearchResults([]);
      setUserPermission('viewer');
      loadShares();
    } catch (error) {
      errorNotification(error.response?.data?.message || 'Failed to share');
    }
  };

  const handleShareWithOrg = async () => {
    try {
      await shareService.createShare(dataCaptureId, userOrganizationId, 'organization', orgPermission);
      infoNotification('Shared with organization');
      setOrgPermission('viewer');
      loadShares();
    } catch (error) {
      errorNotification(error.response?.data?.message || 'Failed to share');
    }
  };

  const handleRevoke = async (shareId) => {
    try {
      await shareService.revokeShare(shareId);
      infoNotification('Access revoked');
      loadShares();
    } catch (error) {
      errorNotification(error.response?.data?.message || 'Failed to revoke');
    }
  };

  const handleUpdatePermission = async (shareId, newPermission) => {
    try {
      await shareService.updatePermission(shareId, newPermission);
      infoNotification('Permission updated');
      loadShares();
    } catch (error) {
      errorNotification(error.response?.data?.message || 'Failed to update permission');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Share Recording</DialogTitle>
      <DialogContent>
        {!hasOrg ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            You must be in an organization to share recordings.
          </Typography>
        ) : (
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Share with a team member */}
            <div>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Share with a team member
              </Typography>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={searchResults}
                  getOptionLabel={(option) => `${option.username} (${option.email})`}
                  value={selectedGrantee}
                  onChange={(e, val) => setSelectedGrantee(val)}
                  inputValue={searchQuery}
                  onInputChange={(e, val) => setSearchQuery(val)}
                  loading={searchLoading}
                  renderInput={(params) => (
                    <TextField {...params} label="Search organization members..." size="small" />
                  )}
                  noOptionsText={searchQuery.length < 2 ? 'Type to search...' : 'No results'}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Permission</InputLabel>
                  <Select
                    value={userPermission}
                    label="Permission"
                    onChange={(e) => setUserPermission(e.target.value)}
                  >
                    <MenuItem value="viewer">Viewer</MenuItem>
                    <MenuItem value="editor">Editor</MenuItem>
                    <MenuItem value="full_access">Full Access</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="contained" onClick={handleShareWithUser} disabled={!selectedGrantee}>
                  Share
                </Button>
              </Stack>
            </div>

            <Divider />

            {/* Share with entire organization */}
            <div>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Share with your entire organization
              </Typography>
              {orgAlreadyShared ? (
                <Typography variant="body2" color="text.secondary">
                  Already shared with your organization (see below).
                </Typography>
              ) : (
                <Stack direction="row" spacing={2} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Permission</InputLabel>
                    <Select
                      value={orgPermission}
                      label="Permission"
                      onChange={(e) => setOrgPermission(e.target.value)}
                    >
                      <MenuItem value="viewer">Viewer</MenuItem>
                      <MenuItem value="editor">Editor</MenuItem>
                      <MenuItem value="full_access">Full Access</MenuItem>
                    </Select>
                  </FormControl>
                  <Button variant="outlined" onClick={handleShareWithOrg}>
                    Share with Organization
                  </Button>
                </Stack>
              )}
            </div>

            <Divider />

            {/* Current shares */}
            {loading ? (
              <CircularProgress sx={{ alignSelf: 'center' }} />
            ) : shares.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Shared With</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Permission</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shares.map((share) => (
                      <TableRow key={share.id}>
                        <TableCell>{share.granteeId}</TableCell>
                        <TableCell>
                          <Chip
                            label={share.granteeType === 'user' ? 'User' : 'Org'}
                            size="small"
                            color={share.granteeType === 'user' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={share.permission}
                            size="small"
                            onChange={(e) => handleUpdatePermission(share.id, e.target.value)}
                          >
                            <MenuItem value="viewer">Viewer</MenuItem>
                            <MenuItem value="editor">Editor</MenuItem>
                            <MenuItem value="full_access">Full Access</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton color="error" size="small" onClick={() => handleRevoke(share.id)}>
                            <Iconify icon="eva:close-fill" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Not shared with anyone yet
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
